import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import logger from "../utils/logging";
import UserAuthService from "../services/UserAuthService";

interface LoginProps {
    authService: UserAuthService;
}

const Login: React.FC<LoginProps> = (props) => {
    const { authService } = props;
    const { t } = useTranslation(["auth", "common"]);

    const [isLoading, setLoading] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string>("");

    const navigate = useNavigate();
    const location = useLocation();

    const checkAndRedirect = () => {
        if (authService.isInitialized() && authService.isLoggedIn()) {
            const from = location.state?.from?.pathname
                ? location.state.from.pathname + (location.state.from.search || "")
                : "/";
            navigate(from, { replace: true });
        }
    };

    useEffect(() => {
        const unsubscribeInit = authService.onInitializationChanged(checkAndRedirect);
        const unsubscribeAuth = authService.onAuthStateChanged(checkAndRedirect);

        // Check immediately
        checkAndRedirect();

        return () => {
            unsubscribeInit();
            unsubscribeAuth();
        };
    }, [authService, navigate, location.state]);

    const handleSubmit = () => {
        setLoading(true);

        // Get the intended redirect location from ProtectedRoute
        const from = location.state?.from?.pathname + (location.state?.from?.search || "") || "/";

        authService
            .login(from)
            .then(() => {
                logger.info("keycloak login finished");
                setLoading(false);
                if (authService.isLoggedIn()) {
                    // If login completed without redirect (silent SSO), navigate directly
                    navigate(from);
                } else {
                    setLoginError(t("errors.loginFailed"));
                }
            })
            .catch((error: unknown) => {
                logger.error("keycloak login failed", error);
                setLoginError(getErrorMessage(error));
                setLoading(false);
            });
    };

    function getErrorMessage(error: unknown): string {
        if (!error) {
            return t("errors.unknownError");
        }
        if (error instanceof Error) {
            return error.message;
        }
        return JSON.stringify(error);
    }

    return (
        <div className="login-view d-flex flex-column justify-content-center align-items-center text-center">
            <h1 className="display-4 mb-3">{t("common:base.title")}</h1>
            <p className="mb-4">{t("login.subtitle")}</p>
            {!isLoading ? (
                <>
                    <Button type="submit" variant="primary" onClick={handleSubmit} style={{ width: "auto" }}>
                        {t("login.button")}
                    </Button>
                    {loginError && (
                        <Alert variant="danger" onClose={() => setLoginError("")} dismissible className="mt-3 w-100 w-md-auto">
                            <Alert.Heading>{t("login.errorTitle")}</Alert.Heading>
                            <p>{loginError}</p>
                        </Alert>
                    )}
                </>
            ) : (
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t("login.loading")}</span>
                </Spinner>
            )}
        </div>
    );
};

export default Login;
